'use client';

import { useEffect, useRef, useState } from 'react';

export type ScrollAnimationType =
  | 'fade-in-up'
  | 'fade-in-down'
  | 'fade-in-left'
  | 'fade-in-right'
  | 'scale-in'
  | 'slide-in-bottom';

interface ScrollAnimationOptions {
  threshold?: number;
  delay?: number;
  duration?: string;
  triggerOnce?: boolean;
}

/**
 * Premium scroll animation hook
 * Triggers animations when elements come into view
 * Creates elegant, sophisticated interactions
 */
export function useScrollAnimation<T extends HTMLElement = HTMLDivElement>(
  type: ScrollAnimationType = 'fade-in-up',
  options: ScrollAnimationOptions = {}
) {
  const {
    threshold = 0.1,
    delay = 0,
    duration = '0.6s',
    triggerOnce = true,
  } = options;

  const elementRef = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce && elementRef.current) {
            observer.unobserve(elementRef.current);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [threshold, triggerOnce]);

  const animationStyle: React.CSSProperties = isVisible
    ? {
      animation: `${type} ${duration} ease-out forwards`,
      animationDelay: `${delay}ms`,
    }
    : {
      opacity: 0,
    };

  return { ref: elementRef, isVisible, style: animationStyle };
}

/**
 * Staggered scroll animation for lists
 * Applies animation with increasing delays to create stagger effect
 */
export function useStaggeredScrollAnimation(
  type: ScrollAnimationType = 'fade-in-up',
  options: ScrollAnimationOptions = {}
) {
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());

  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const indexStr = (entry.target as HTMLElement).dataset.index;
          if (indexStr !== undefined) {
            const index = parseInt(indexStr, 10);
            if (entry.isIntersecting) {
              setVisibleItems((prev) => new Set(prev).add(index));
              if (options.triggerOnce !== false) {
                observerRef.current?.unobserve(entry.target);
              }
            } else if (!options.triggerOnce) {
              setVisibleItems((prev) => {
                const next = new Set(prev);
                next.delete(index);
                return next;
              });
            }
          }
        });
      },
      { threshold: options.threshold || 0.1 }
    );

    return () => {
      observerRef.current?.disconnect();
    };
  }, [options.threshold, options.triggerOnce]);

  const getItemProps = (index: number) => {
    const isVisible = visibleItems.has(index);
    const delay = (options.delay || 0) + (index % 8) * 80; // Added modulo to prevent massive delays in long lists

    return {
      'data-index': index,
      ref: (el: HTMLElement | null) => {
        if (el) {
          itemRefs.current.set(index, el);
          observerRef.current?.observe(el);
        } else {
          const prevEl = itemRefs.current.get(index);
          if (prevEl) {
            observerRef.current?.unobserve(prevEl);
          }
          itemRefs.current.delete(index);
        }
      },
      style: isVisible
        ? ({
          animation: `${type} ${options.duration || '0.6s'} ease-out forwards`,
          animationDelay: `${delay}ms`,
        } as React.CSSProperties)
        : ({ opacity: 0 } as React.CSSProperties),
    };
  };

  return { getItemProps };
}

/**
 * Smooth scroll to element
 * Premium smooth scrolling behavior
 */
export function scrollToElement(
  elementId: string,
  offset: number = 80,
  behavior: ScrollBehavior = 'smooth'
) {
  const element = document.getElementById(elementId);
  if (element) {
    const top = element.offsetTop - offset;
    window.scrollTo({
      top,
      behavior,
    });
  }
}

/**
 * Global scroll animation wrapper
 * Wraps multiple scroll animations with stagger
 */
export function useScrollAnimations(
  elements: React.RefObject<HTMLElement>[],
  type: ScrollAnimationType = 'fade-in-up',
  options: ScrollAnimationOptions = {}
) {
  const [visibleElements, setVisibleElements] = useState<Set<number>>(new Set());

  useEffect(() => {
    const observers = elements.map((ref, index) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => new Set(prev).add(index));
            if (options.triggerOnce !== false) {
              observer.disconnect();
            }
          } else if (!options.triggerOnce) {
            setVisibleElements((prev) => {
              const next = new Set(prev);
              next.delete(index);
              return next;
            });
          }
        },
        { threshold: options.threshold || 0.1 }
      );

      if (ref.current) {
        observer.observe(ref.current);
      }

      return observer;
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [elements, options.threshold, options.triggerOnce]);

  const getAnimationStyle = (index: number): React.CSSProperties => {
    const isVisible = visibleElements.has(index);
    const delay = (options.delay || 0) + index * 100;

    return isVisible
      ? {
        animation: `${type} ${options.duration || '0.6s'} ease-out forwards`,
        animationDelay: `${delay}ms`,
      }
      : {
        opacity: 0,
      };
  };

  return { getAnimationStyle, visibleElements };
}
